import React, { Component } from "react";
import axios from 'axios';
import { authService } from '../../services/authService';
import { CaretRightOutlined, DeleteOutlined } from '@ant-design/icons';
import { Collapse, theme, Row, Col, Card, InputNumber, Button, Input, Divider, Modal, Grid, Spin } from 'antd';
import { API_BASE_URL } from '../../config/api';

const { useBreakpoint } = Grid;

const CATEGORIES = [
  'REQUISITION FORMS',
  'VACUTAINER TUBES',
  'URINE',
  'SWABS',
  'MICROBIOLOGY',
  'CYTOLOGY/HISTOLOGY/MOLECULAR',
  'NEEDLES/TOURNIQUET',
  'GENERAL SUPPLIES',
];

const panelColors = [
  '#1E5FA8',
  '#B03A3A',
  '#C87A1A',
  '#4A7C23',
  '#7a4600',
  '#029ca7',
  '#782fa3',
  '#5A6475',
];

const groupByCategory = (supplies) => {
  return supplies.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});
};

class Order extends Component {
  constructor(props) {
    super(props);
    this.state = {
      errors: [],
      supplies: [],
      grouped: {},
      quantities: {},
      comment: '',
      orderNumber: '',
      orderID: 0,

      saving: false,
    };
  }

  componentDidMount = () => {
    this.loadData();
  }

  loadData = async () => {
    const token = await authService.ensureValidToken();
    if (!token) {
      console.error('Not authenticated');
      return;
    }

    axios({
      method: 'GET',
      url: `${API_BASE_URL}/admin/supplies/?forClients=1`,
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      const supplies = res.data.data;
      const grouped = groupByCategory(supplies);
      this.setState({ supplies, grouped });
    }).catch(err => console.log(err));
  }

  handleQuantityChange = (itemId, itemName, value) => {
    this.setState(prev => ({
      quantities: {
        ...prev.quantities,
        [itemId]: { name: itemName, quantity: value ?? 0 },
      },
    }));
  }

  handleSubmit = async () => {
    this.setState({ errors: [], saving: true });
    const { quantities, comment } = this.state;
    const orderItems = Object.entries(quantities)
      .filter(([_, v]) => v.quantity > 0)
      .map(([id, v]) => ({ id, name: v.name, quantity: v.quantity }));

    let isValid = true;
    let errors = [];
    let formData = { client: this.props.client };

    if (orderItems.length) {
      formData.orderItems = orderItems;
    } else {
      if (comment) {
        formData.comment = comment;
        formData.orderItems = [{ id: 29, name: 'Other', quantity: 1 }];
      } else {
        isValid = false;
        errors.push("Please select supplies");
      }
    }

    formData.comment = comment;
    formData.username = this.props.client.ClientCode;
    formData.prefix = 'LC'

    if (isValid) {
      const token = await authService.ensureValidToken();
      if (!token) {
        console.error('Not authenticated');
        return;
      }

      axios({
        method: 'POST',
        url: `${API_BASE_URL}/admin/supply-orders/`,
        data: formData,
        headers: { Authorization: `Bearer ${token}` },
      }).then(res => {
        this.setState({
          orderID: res.data.data.orderId,
          orderNumber: res.data.data.orderNumber,
          saving: false,
        });
      }).catch(err => console.log(err));
    } else {
      this.setState({ errors, saving: false });
    }
  }

  handleClearAll = () => {
    this.setState({ quantities: {} });
  }

  handleCommentChange = (e) => {
    this.setState({ comment: e.target.value });
  }

  handleOk = () => {
    this.setState({
      errors: [],
      quantities: {},
      comment: '',
      orderNumber: '',
      orderID: 0,
    });
  }

  render() {
    const { token, screens } = this.props;
    const { grouped, quantities, comment } = this.state;
    const isMobile = !screens.md;

    const panelStyle = {
      background: token.colorFillAlter,
      borderRadius: token.borderRadiusLG,
      border: 'none',
    };

    const totalItems = Object.values(quantities).filter(v => v.quantity > 0).length;

    const allItems = CATEGORIES.map((category, index) => ({
      key: String(index + 1),
      label: category,
      children: (
        <div>
          {(grouped[category] || []).map(item => (
            <div
              key={item.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '6px 8px',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <span style={{ fontSize: 14 }}>
                {item.name}, {item.unit}
              </span>
              <InputNumber
                min={0}
                value={quantities[item.id]?.quantity ?? 0}
                onChange={(val) => this.handleQuantityChange(item.id, item.name, val)}
                size="small"
                style={{ width: 80 }}
              />
            </div>
          ))}
        </div>
      ),
    }));

    const buildItems = (items) =>
      items.map((item) => ({
        ...item,
        style: { ...panelStyle, marginBottom: 8 },
        styles: {
          header: {
            background: panelColors[allItems.findIndex(i => i.key === item.key)],
            borderRadius: 6,
            color: '#fff',
            fontWeight: 600,
            padding: '2px 12px',
          },
        },
      }));

    const leftItems  = allItems.slice(0, 4);
    const rightItems = allItems.slice(4, 8);

    const collapseProps = (items, defaultOpen = true) => ({
      bordered: false,
      defaultActiveKey: defaultOpen ? items.map(i => i.key) : [],
      expandIcon: ({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />,
      style: { background: token.colorBgContainer },
      items: buildItems(items),
    });

    const orderSummary = (
      <Card
        title="Order Summary"
        extra={
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={this.handleClearAll}
            disabled={totalItems === 0}
          >
            Clear All
          </Button>
        }
        style={isMobile ? {} : { height: '100%' }}
      >
        {totalItems === 0 ? (
          <div style={{ textAlign: 'center', color: '#bbb', padding: '40px 0', fontSize: 14 }}>
            No items added
          </div>
        ) : (
          <div>
            {Object.entries(quantities)
              .filter(([_, v]) => v.quantity > 0)
              .map(([id, v]) => (
                <div key={id} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '6px 0',
                  borderBottom: '1px solid #f0f0f0',
                  fontSize: 14,
                }}>
                  <span>{v.name}</span>
                  <span style={{ color: '#888' }}>x{v.quantity}</span>
                </div>
              ))
            }
          </div>
        )}
        <div style={{ textAlign: 'right', marginTop: 16, fontWeight: 600, fontSize: 14 }}>
          Total: {totalItems} item{totalItems !== 1 ? 's' : ''}
        </div>
        <Input.TextArea
          placeholder="Add a comment..."
          value={comment}
          onChange={this.handleCommentChange}
          rows={4}
          style={{ marginTop: 12 }}
        />
      </Card>
    );

    return (
      <Row>
        <Col span={24}>
          <Card
            title="Supplies Order Form"
            extra={
              <Button type="primary" onClick={this.handleSubmit}>
                {this.state.saving ? <Spin/> :
                  <span>Submit Order{totalItems > 0 ? ` (${totalItems} item${totalItems > 1 ? 's' : ''})` : ''}</span>
                }
                
              </Button>
            }
          >
            <Row gutter={[8, 8]}>
              <Col xs={24} md={19}>
                {this.props.client?.id ?
                  <>
                    <strong>{this.props.client.ClientName}</strong> - {this.props.client.Address}, {this.props.client.City}, {this.props.client.State} {this.props.client.Zip}
                  </> : <></>
                }
              </Col>
              <Col xs={24} md={5}>
                {this.state.errors.length > 0 &&
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {this.state.errors.map((item, index) =>
                      <li style={{ color: 'red' }} key={index}>{item}</li>
                    )}
                  </ul>
                }
              </Col>
            </Row>

            <Divider />

            {isMobile ? (
              // ── Mobile layout ──────────────────────────────
              <Row gutter={[0, 16]}>
                <Col span={24}>
                  <Collapse {...collapseProps(allItems, false)} />
                </Col>
                <Col span={24}>
                  {orderSummary}
                </Col>
              </Row>
            ) : (
              // ── Desktop layout ─────────────────────────────
              <Row gutter={16}>
                <Col span={8}>
                  <Collapse key={`left-${isMobile}`} {...collapseProps(leftItems, true)} />
                </Col>
                <Col span={8}>
                  <Collapse key={`right-${isMobile}`} {...collapseProps(rightItems, true)} />
                </Col>
                <Col span={8}>
                  {orderSummary}
                </Col>
              </Row>
            )}
          </Card>

          {this.state.orderID ?
            <Modal
              title="Supply Order Confirmation"
              closable={{ 'aria-label': 'Custom Close Button' }}
              open={!!this.state.orderID}
              onOk={this.handleOk}
              onCancel={this.handleOk}
            >
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <span style={{ color: '#cc0000', fontSize: 28, fontWeight: 700 }}>
                  {this.state.orderNumber}
                </span>
              </div>
            </Modal> : <></>
          }
        </Col>
      </Row>
    );
  }
}

export default function OrderWrapper(props) {
  const { token } = theme.useToken();
  const screens = useBreakpoint();
  return <Order {...props} token={token} screens={screens} />;
}